import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { geocodeSubscriberAddress } from "../../services/geocodingService";
import { SERVICE_AREA_CENTER, SERVICE_AREA_ZOOM } from "../../data/barangayGeodata";

/**
 * PublicLocationPickerModal
 *
 * Full-screen, mobile-friendly map modal used on the public migration request
 * form. On open it geocodes the subscriber's typed municipality / barangay /
 * address and flies the satellite map there (the same hierarchical lookup used
 * when clicking a subscriber name on the internal dispatch map). The subscriber
 * then drags the pin — or taps the map — onto their exact house and confirms.
 * Confirming returns { lat, lng } to the parent form.
 */

// Glowing house pin (divIcon avoids the Leaflet default-marker bundler issue).
const PIN_ICON = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:44px;height:44px;border-radius:50%;
      background:#dc2626;border:4px solid #fff;
      box-shadow:0 4px 14px rgba(220,38,38,0.55);
    "><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:4px;height:10px;background:#dc2626;"></div>
    <div style="width:12px;height:12px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 6px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [44, 70],
  iconAnchor: [22, 70],
});

// Flies to a target once, and keeps the map sized correctly inside the modal.
function MapController({ flyTarget }) {
  const map = useMap();
  useEffect(() => {
    // Modals mount the map before its container has its final size; nudge it.
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (flyTarget) {
      map.flyTo([flyTarget.lat, flyTarget.lng], flyTarget.zoom || 18, { duration: 1 });
    }
  }, [flyTarget, map]);
  return null;
}

// Draggable pin + tap-to-move.
function LocationMarker({ position, setPosition }) {
  const markerRef = useRef(null);
  useMapEvents({
    click(e) {
      setPosition({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const ll = marker.getLatLng();
          setPosition({ lat: ll.lat, lng: ll.lng });
        }
      },
    }),
    [setPosition]
  );
  if (!position) return null;
  return (
    <Marker
      draggable
      position={[position.lat, position.lng]}
      ref={markerRef}
      icon={PIN_ICON}
      eventHandlers={eventHandlers}
    />
  );
}

export default function PublicLocationPickerModal({
  open,
  initial, // { lat, lng } | null — existing pin to re-edit
  municipality,
  barangay,
  address,
  onConfirm,
  onClose,
}) {
  const [position, setPosition] = useState(initial || null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [locating, setLocating] = useState(false);
  const [hint, setHint] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);

  // On open: seed the pin. Use an existing pin if re-editing, otherwise geocode
  // the typed address and drop the pin there as a starting point.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    if (initial?.lat != null && initial?.lng != null) {
      setPosition({ lat: initial.lat, lng: initial.lng });
      setFlyTarget({ lat: initial.lat, lng: initial.lng, zoom: 19 });
      setHint("Drag the pin or tap the map to adjust your exact house location.");
      return;
    }

    setLocating(true);
    setHint("Finding your address on the map…");
    (async () => {
      try {
        const geo = await geocodeSubscriberAddress({
          address: address || "",
          barangay: barangay || "",
          municipality: municipality || "",
          latitude: null,
          longitude: null,
        });
        if (cancelled) return;
        if (geo?.success && geo.latitude && geo.longitude) {
          setPosition({ lat: geo.latitude, lng: geo.longitude });
          setFlyTarget({ lat: geo.latitude, lng: geo.longitude, zoom: 18 });
          setHint("Drag the pin or tap the map onto your exact house, then confirm.");
        } else {
          setHint("We couldn't find the address automatically. Tap the map on your exact house location.");
        }
      } catch {
        if (!cancelled) {
          setHint("Tap the map on your exact house location, then confirm.");
        }
      } finally {
        if (!cancelled) setLocating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally only re-run when the modal transitions to open; the typed
    // address fields are read once at open time.
  }, [open]);

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      setHint("Location services are not available on this device.");
      return;
    }
    setGpsBusy(true);
    setHint("Getting your current location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition({ lat, lng });
        setFlyTarget({ lat, lng, zoom: 19 });
        setHint("Drag the pin to fine-tune your exact house, then confirm.");
        setGpsBusy(false);
      },
      () => {
        setHint("Couldn't get your location. Please allow location access or tap your house on the map.");
        setGpsBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex flex-col bg-black/50">
      <div className="flex flex-col w-full h-full sm:max-w-2xl sm:h-[85vh] sm:my-auto sm:mx-auto sm:rounded-xl overflow-hidden bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
          <div>
            <div className="text-sm font-bold text-gray-800">Pin Your Exact Location</div>
            <div className="text-[11px] text-gray-500">
              {[barangay, municipality].filter(Boolean).join(", ") || "Set your house location"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Map */}
        <div className="relative flex-1 min-h-0">
          <MapContainer
            center={position ? [position.lat, position.lng] : SERVICE_AREA_CENTER}
            zoom={position ? 18 : SERVICE_AREA_ZOOM}
            maxZoom={21}
            className="w-full h-full"
            zoomControl={true}
          >
            <MapController flyTarget={flyTarget} />
            {/* Google satellite imagery + place/road labels for orientation */}
            <TileLayer
              attribution="&copy; Google"
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              maxZoom={21}
            />
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
              pane="overlayPane"
              maxZoom={21}
            />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>

          {/* Instruction banner */}
          {hint && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] max-w-[90%] bg-white/95 rounded-lg px-3 py-1.5 shadow text-[11px] sm:text-xs text-gray-700 text-center pointer-events-none">
              {locating ? "Finding your address on the map…" : hint}
            </div>
          )}

          {/* Locate-me button (helpful on mobile) */}
          <button
            type="button"
            onClick={handleLocateMe}
            disabled={gpsBusy}
            className="absolute bottom-3 right-3 z-[1000] flex items-center gap-1.5 bg-white rounded-lg shadow-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 active:bg-gray-50 disabled:opacity-60"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8a4 4 0 100 8 4 4 0 000-8z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v3M12 19v3M22 12h-3M5 12H2" />
            </svg>
            {gpsBusy ? "Locating…" : "Use my location"}
          </button>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-white shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] text-gray-500 font-mono">
              {position
                ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
                : "No pin set yet"}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 active:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => position && onConfirm({ lat: position.lat, lng: position.lng })}
                disabled={!position}
                className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                Confirm Location
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
